import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useEffectiveCity } from '../hooks/useEffectiveCity';
import {
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
  fetchAllPaginated,
  fetchSchoolsSimple,
  API_BASE_URL,
} from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import RefreshButton from '../components/RefreshButton';
import SearchableSelect from '../components/SearchableSelect';
import DashboardRH from '../components/funcionarios/DashboardRH';
import ImportarFuncionariosModal from '../components/ImportarFuncionariosModal';
import { useDeleteModal } from '../hooks/useDeleteModal';
import { includesNormalized, normalizePersonName } from '../utils/normalizeText';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Ativo' },
  { value: 'licenca_maternidade', label: 'Licença Maternidade/Paternidade' },
  { value: 'licenca_medica', label: 'Licença Médica' },
  { value: 'licenca_sem_vencimento', label: 'Licença sem Vencimento' },
  { value: 'licenca_estudo', label: 'Licença para Estudo (Mestrado/Doutorado)' },
  { value: 'cedido', label: 'Cedido' },
  { value: 'readaptado', label: 'Readaptado(a)' },
  { value: 'inactive', label: 'Inativo' },
];

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: 'estatutario', label: 'Estatutário (Efetivo)' },
  { value: 'comissionado', label: 'Comissionado' },
  { value: 'contratado', label: 'Contratado' },
  { value: 'terceirizado', label: 'Terceirizado' },
];

const STATUS_LABELS = STATUS_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {});
const EMPLOYMENT_TYPE_LABELS = EMPLOYMENT_TYPE_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {});

// ============================================================
// Perfis de sistema disponíveis para criação de usuário
// ============================================================
const SYSTEM_ROLE_OPTIONS = [
  { value: 'administrador',    label: 'Administrador (Gestão Estratégica)' },
  { value: 'secretario',       label: 'Secretário (Gestão Estratégica)' },
  { value: 'coordenador',      label: 'Coordenador (Gestão Pedagógica)' },
  { value: 'gestor',           label: 'Gestor (Secretário Executivo da SME)' },
  { value: 'diretor',          label: 'Diretor(a) Escolar' },
  { value: 'professor',        label: 'Professor' },
  { value: 'supervisor',       label: 'Supervisor' },
  { value: 'referencia_tecnica', label: 'Referência Técnica Municipal' },
];

// Mapeamento: slug do cargo → perfil de sistema sugerido
const SLUG_TO_SYSTEM_ROLE = {
  // Secretário (cargo de Gestão Estratégica)
  secretario_municipal_educacao: 'secretario',
  // Gestor: Secretário(a) Executivo(a) da SME — leitura da rede inteira.
  secretario_executivo_educacao: 'gestor',
  // Coordenador
  diretor_ensino:               'coordenador',
  coord_educacao_infantil:      'coordenador',
  coord_ensino_fundamental:     'coordenador',
  coord_eja:                    'coordenador',
  coord_educacao_especial_aee:  'coordenador',
  coord_educacao_integral:      'coordenador',
  coord_formacao_continuada:    'coordenador',
  // Gestor
  coord_rh:                       'gestor',
  coord_folha_pagamento:          'gestor',
  coord_lotacao_movimentacao:     'gestor',
  coord_formacao_desenvolvimento: 'gestor',
  // Direção da escola: acesso preso à escola dela. Antes estes cargos
  // sugeriam "Gestor", que enxerga o município inteiro — foi assim que os
  // diretores entraram no sistema vendo todas as escolas da rede.
  diretor:                        'diretor',
  vice_diretor:                   'diretor',
  coordenador_pedagogico:         'supervisor',
  // Professor
  professor: 'professor',
  // Supervisor
  supervisor: 'supervisor',
};

// ============================================================
// Categorias de cargos (estrutura organizacional da SME)
// ============================================================
// Mapeamento slug → categoria. Slugs novos vêm da migration
// 0012_add_full_organizational_roles. Para qualquer slug
// desconhecido, usa-se a faixa de `order` como fallback.
const CARGO_CATEGORIES = [
  { key: 'gestao_estrategica',     label: '1. Gestão Estratégica',                      orderRange: [10, 99] },
  { key: 'pedagogica',             label: '2a. Diretorias — Gestão Pedagógica',         orderRange: [100, 199] },
  { key: 'administrativa',         label: '2b. Diretorias — Administrativa e Financeira', orderRange: [200, 299] },
  { key: 'rh',                     label: '2c. Diretorias — Gestão de Pessoas (RH)',     orderRange: [300, 399] },
  { key: 'infraestrutura',         label: '2d. Diretorias — Infraestrutura e Logística', orderRange: [400, 499] },
  { key: 'tecnologia',             label: '2e. Diretorias — Tecnologia e Dados',         orderRange: [500, 599] },
  { key: 'programas',              label: '2f. Diretorias — Programas e Projetos',       orderRange: [600, 699] },
  { key: 'orgaos_vinculados',      label: '3. Órgãos Vinculados',                        orderRange: [700, 799] },
  { key: 'escolas',                label: '4. Estrutura nas Escolas',                    orderRange: [800, 899] },
  { key: 'nucleos',                label: '5. Funções Estratégicas Modernas',            orderRange: [900, 999] },
  { key: 'outros',                 label: 'Outros',                                      orderRange: [-Infinity, Infinity] },
];

const SLUG_TO_CATEGORY = {
  // 1) Gestão Estratégica
  secretario_municipal_educacao: 'gestao_estrategica',
  secretario_executivo_educacao: 'gestao_estrategica',
  chefia_gabinete: 'gestao_estrategica',
  assessoria_juridica: 'gestao_estrategica',
  assessor_tecnico_gabinete: 'gestao_estrategica',
  secretario_gabinete: 'gestao_estrategica',
  assessoria_comunicacao: 'gestao_estrategica',
  // 2a) Pedagógica
  diretor_ensino: 'pedagogica',
  coord_educacao_infantil: 'pedagogica',
  coord_ensino_fundamental: 'pedagogica',
  coord_eja: 'pedagogica',
  coord_educacao_especial_aee: 'pedagogica',
  coord_educacao_integral: 'pedagogica',
  coord_formacao_continuada: 'pedagogica',
  // 2b) Administrativa e Financeira
  diretor_administrativo: 'administrativa',
  diretor_financeiro: 'administrativa',
  coord_orcamento: 'administrativa',
  coord_compras_licitacoes: 'administrativa',
  coord_contratos_convenios: 'administrativa',
  coord_prestacao_contas: 'administrativa',
  // 2c) RH
  coord_rh: 'rh',
  coord_folha_pagamento: 'rh',
  coord_lotacao_movimentacao: 'rh',
  coord_formacao_desenvolvimento: 'rh',
  // 2d) Infraestrutura
  coord_infraestrutura_escolar: 'infraestrutura',
  coord_manutencao_obras: 'infraestrutura',
  coord_transporte_escolar: 'infraestrutura',
  coord_alimentacao_escolar: 'infraestrutura',
  // 2e) Tecnologia/Dados
  coord_tecnologia_informacao: 'tecnologia',
  coord_censo_escolar: 'tecnologia',
  coord_indicadores_monitoramento: 'tecnologia',
  // 2f) Programas
  coord_programas_federais: 'programas',
  coord_projetos_estrategicos: 'programas',
  coord_parcerias_convenios: 'programas',
  // 3) Órgãos Vinculados
  conselho_municipal_educacao: 'orgaos_vinculados',
  conselho_cacs_fundeb: 'orgaos_vinculados',
  // 4) Estrutura nas Escolas
  diretor: 'escolas',
  vice_diretor: 'escolas',
  coordenador_pedagogico: 'escolas',
  professor: 'escolas',
  supervisor: 'escolas',
  secretaria: 'escolas',
  auxiliar_administrativo: 'escolas',
  assistente_administrativo: 'escolas',
  tecnico_administrativo: 'escolas',
  merendeira: 'escolas',
  porteiro: 'escolas',
  vigia: 'escolas',
  motorista: 'escolas',
  profissional_apoio_aee: 'escolas',
  auxiliar: 'escolas',
  outros: 'escolas',
  // 5) Núcleos
  nucleo_curriculo_bncc: 'nucleos',
  nucleo_avaliacao_resultados: 'nucleos',
  nucleo_educacao_digital: 'nucleos',
  nucleo_inclusao_diversidade: 'nucleos',
  nucleo_busca_ativa: 'nucleos',
  nucleo_primeira_infancia: 'nucleos',
};

function categorizeCargo(cargo) {
  const direct = SLUG_TO_CATEGORY[cargo.slug];
  if (direct) return direct;
  const order = Number(cargo.order ?? 0);
  for (const cat of CARGO_CATEGORIES) {
    const [min, max] = cat.orderRange;
    if (order >= min && order <= max) return cat.key;
  }
  return 'outros';
}

function groupCargos(cargos) {
  const buckets = new Map(CARGO_CATEGORIES.map(c => [c.key, { ...c, items: [] }]));
  cargos.forEach(c => {
    const key = categorizeCargo(c);
    if (buckets.has(key)) buckets.get(key).items.push(c);
  });
  cargos.length === 0 && buckets.clear();
  return Array.from(buckets.values()).filter(g => g.items.length > 0);
}

/**
 * Página de gestão de RH (Funcionários) — acessível apenas ao perfil Admin.
 * Permite cadastrar professores, coordenadores, diretores, técnicos
 * administrativos, auxiliares, merendeiras e outros funcionários da escola.
 */
function Funcionarios() {
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const effectiveCity = useEffectiveCity();
  const cityId = effectiveCity?.id || null;

  // Estados principais
  const [funcionarios, setFuncionarios] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [escolas, setEscolas] = useState([]);
  const [teacherSchools, setTeacherSchools] = useState([]);
  const [disciplinas, setDisciplinas] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [teacherSubjects, setTeacherSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Visualização: 'dashboard' | 'lista'
  const [view, setView] = useState('dashboard');

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroCargo, setFiltroCargo] = useState('');

  // Modal cadastro/edição
  const [showModal, setShowModal] = useState(false);
  const [importando, setImportando] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    teacher_name: '',
    teacher_serial: '',
    cpf: '',
    rg: '',
    phone: '',
    email: '',
    hire_date: '',
    id_role: '',
    status: 'active',
    employment_type: '',
    schools: [],
    subjects: [],
    classes: [],
    observations: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Acesso ao sistema
  const [createUserAccess, setCreateUserAccess] = useState(false);
  // Uma pessoa pode acumular funções: cada perfil marcado vira um acesso
  // separado, com o seu login, as suas permissões e o seu próprio histórico.
  const [userProfiles, setUserProfiles] = useState([]);
  const alternarPerfilAcesso = (valor) =>
    setUserProfiles((atuais) =>
      atuais.includes(valor) ? atuais.filter((p) => p !== valor) : [...atuais, valor]
    );
  const [createdUserInfo, setCreatedUserInfo] = useState(null);
  // Contas de acesso do funcionário em edição (lista vazia = não tem).
  // É lista porque quem acumula função tem uma conta por perfil: a diretora
  // que também dá aula entra como diretora OU como professora, e são logins
  // diferentes. Guardar só a primeira escondia metade dos acessos dela.
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [creatingAccess, setCreatingAccess] = useState(false);
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  // Aviso de possível cadastro duplicado (comparação sem acento/caixa)
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [showDuplicatesPanel, setShowDuplicatesPanel] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [duplicatesError, setDuplicatesError] = useState(null);
  const [merging, setMerging] = useState(null);

  // Modal detalhes
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Modal exclusão
  const {
    isDeleteModalOpen,
    itemToDelete,
    openDeleteModal,
    closeDeleteModal,
  } = useDeleteModal();

  // Fetch inicial
  // `silencioso` relê sem trocar a página pelo spinner. A janela de importar
  // planilha chama assim: o spinner desmontava a janela junto, e o relatório
  // da importação sumia no instante em que deveria aparecer.
  const fetchData = useCallback(async (opcoes) => {
    const silencioso = opcoes?.silencioso === true;
    if (!silencioso) setLoading(true);
    setError(null);
    try {
      const cityParams = cityId ? { city: cityId } : {};
      const [funcsData, cargosData, escolasData, teacherSchoolsData, disciplinasData, turmasData, teacherSubjectsData] = await Promise.all([
        fetchAllPaginated('/employees/', cityParams),
        apiGet('/employee-roles/'),
        fetchSchoolsSimple(cityId),
        fetchAllPaginated('/teacher-schools/', cityParams),
        fetchAllPaginated('/subjects/'),
        fetchAllPaginated('/classes/', cityParams),
        // /teacher-subjects/ não filtra por cidade (só por id_teacher/id_subject);
        // o cruzamento é feito no cliente pelo id do funcionário.
        fetchAllPaginated('/teacher-subjects/'),
      ]);
      setFuncionarios(funcsData || []);
      setCargos(Array.isArray(cargosData) ? cargosData : (cargosData?.results || []));
      setEscolas(escolasData || []);
      setTeacherSchools(teacherSchoolsData || []);
      setDisciplinas(disciplinasData || []);
      setTurmas(turmasData || []);
      setTeacherSubjects(teacherSubjectsData || []);
    } catch (err) {
      console.error('Erro ao carregar funcionários:', err);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [cityId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-sugere perfil de sistema ao selecionar cargo (somente no cadastro)
  useEffect(() => {
    if (!editingEmployee && formData.id_role) {
      const cargo = cargos.find((c) => c.id === formData.id_role);
      const suggested = cargo ? (SLUG_TO_SYSTEM_ROLE[cargo.slug] || '') : '';
      setUserProfiles(suggested ? [suggested] : []);
      setCreateUserAccess(!!suggested);
    }
  }, [formData.id_role, editingEmployee, cargos]);

  // Helpers
  const getEmployeeSchools = useCallback(
    (employeeId) =>
      teacherSchools
        .filter((ts) => ts.id_teacher === employeeId && ts.status === 'active')
        .map((ts) => {
          const escola = escolas.find((e) => e.id === ts.id_school);
          return escola
            ? { id: escola.id, name: escola.school }
            : { id: ts.id_school, name: ts.school_name || 'Escola não encontrada' };
        }),
    [teacherSchools, escolas]
  );

  const getEmployeeSchoolIds = useCallback(
    (employeeId) =>
      teacherSchools
        .filter((ts) => ts.id_teacher === employeeId && ts.status === 'active')
        .map((ts) => ts.id_school),
    [teacherSchools]
  );

  // Disciplinas já vinculadas ao funcionário (tb_teacher_subject)
  const getEmployeeSubjectIds = useCallback(
    (employeeId) =>
      teacherSubjects
        .filter((ts) => ts.id_teacher === employeeId)
        .map((ts) => ts.id_subject),
    [teacherSubjects]
  );

  // Turmas em que o funcionário é regente (TbClass.id_teacher)
  const getEmployeeClassIds = useCallback(
    (employeeId) => turmas.filter((t) => t.id_teacher === employeeId).map((t) => t.id),
    [turmas]
  );

  // O cargo selecionado é docente? (blocos de disciplinas/turmas só aparecem então)
  const isProfessorRole = useMemo(() => {
    const cargo = cargos.find((c) => c.id === formData.id_role);
    return !!cargo && String(cargo.slug || '').includes('professor');
  }, [cargos, formData.id_role]);

  // Turmas oferecidas: restritas às escolas marcadas no formulário
  const turmasDisponiveis = useMemo(() => {
    if (!formData.schools.length) return [];
    return turmas
      .filter((t) => formData.schools.includes(t.id_school))
      .sort((a, b) => (a.class_name || '').localeCompare(b.class_name || ''));
  }, [turmas, formData.schools]);

  // Máscara CPF: 000.000.000-00
  const formatCpf = (value) => {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  // Máscara telefone: (00) 00000-0000
  const formatPhone = (value) => {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 10) {
      return digits
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  };

  // Filtro combinado
  const filteredFuncionarios = useMemo(() => {
    return funcionarios.filter((f) => {
      // Busca sem acento e sem caixa: quem digita "andrea" precisa
      // encontrar "ANDRÉA" — senão recadastra a pessoa duplicada.
      const matchSearch =
        !searchTerm ||
        includesNormalized(f.teacher_name, searchTerm) ||
        String(f.teacher_serial || '').includes(searchTerm) ||
        (f.cpf || '').includes(searchTerm) ||
        includesNormalized(f.email, searchTerm);
      const matchStatus = !filtroStatus || f.status === filtroStatus;
      const matchCargo = !filtroCargo || String(f.id_role || '') === String(filtroCargo);
      return matchSearch && matchStatus && matchCargo;
    });
  }, [funcionarios, searchTerm, filtroStatus, filtroCargo]);

  // Cargos agrupados por categoria (para os dropdowns)
  const cargosAgrupados = useMemo(() => groupCargos(cargos), [cargos]);

  // Estatísticas
  const stats = useMemo(() => {
    const total = funcionarios.length;
    const ativos = funcionarios.filter((f) => f.status === 'active').length;
    const inativos = funcionarios.filter((f) => f.status === 'inactive').length;
    const porCargo = {};
    funcionarios.forEach((f) => {
      const key = f.role_name || 'Sem cargo';
      porCargo[key] = (porCargo[key] || 0) + 1;
    });
    return { total, ativos, inativos, porCargo };
  }, [funcionarios]);

  // Criar
  const handleCreate = () => {
    if (user?.role === ROLES.ADMIN && !cityId) {
      alert('Selecione uma cidade no menu lateral antes de cadastrar um funcionário.');
      return;
    }
    setEditingEmployee(null);
    const cargoPadrao = cargos.find((c) => c.slug === 'professor');
    setFormData({
      teacher_name: '',
      teacher_serial: '',
      cpf: '',
      rg: '',
      phone: '',
      email: '',
      hire_date: '',
      id_role: cargoPadrao ? cargoPadrao.id : '',
      status: 'active',
      employment_type: '',
      schools: [],
      subjects: [],
      classes: [],
      observations: '',
    });
    setCreateUserAccess(false);
    setUserProfiles([]);
    setCreatedUserInfo(null);
    setLinkedAccounts([]);
    setDuplicateWarning(null);
    setFormErrors({});
    setShowModal(true);
  };

  // Editar
  //
  // O formulário é preenchido com o registro COMPLETO vindo da API, não com
  // a linha da listagem: a listagem é enxuta e, se um campo não vier nela,
  // o PUT seguinte gravaria null por cima do que já estava salvo (foi o que
  // acontecia com RG e Observações).
  const handleEdit = async (employee) => {
    setEditingEmployee(employee);
    setFormErrors({});
    setCreatedUserInfo(null);
    setCreateUserAccess(false);
    setUserProfiles([]);
    setShowModal(true);
    setLoadingEmployee(true);
    // Mostra já o que a listagem tem, para o modal não abrir com os dados do
    // funcionário anterior, e completa com o registro cheio logo em seguida.
    setFormData((prev) => ({
      ...prev,
      teacher_name: employee.teacher_name || '',
      teacher_serial: employee.teacher_serial || '',
      cpf: employee.cpf || '',
      rg: '',
      phone: employee.phone || '',
      email: employee.email || '',
      hire_date: employee.hire_date || '',
      id_role: employee.id_role || '',
      status: employee.status || 'active',
      employment_type: employee.employment_type || '',
      schools: [],
      subjects: [],
      classes: [],
      observations: '',
    }));
    let full = employee;
    try {
      full = await apiGet(`/employees/${employee.id}/`);
      setEditingEmployee(full);
    } catch (err) {
      console.error('Erro ao carregar o funcionário; usando os dados da listagem:', err);
    } finally {
      setLoadingEmployee(false);
    }
    setLinkedAccounts(full.user_accounts || (full.user_details ? [full.user_details] : []));
    setFormData({
      teacher_name: full.teacher_name || '',
      teacher_serial: full.teacher_serial || '',
      cpf: full.cpf || '',
      rg: full.rg || '',
      phone: full.phone || '',
      email: full.email || '',
      hire_date: full.hire_date || '',
      id_role: full.id_role || '',
      status: full.status || 'active',
      employment_type: full.employment_type || '',
      schools: full.school_details
        ? full.school_details.map((e) => e.id)
        : getEmployeeSchoolIds(employee.id),
      subjects: full.subject_details
        ? full.subject_details.map((d) => d.id)
        : getEmployeeSubjectIds(employee.id),
      classes: full.class_details
        ? full.class_details.map((c) => c.id)
        : getEmployeeClassIds(employee.id),
      observations: full.observations || '',
    });
  };

  // Ver detalhes — mesma razão do handleEdit: busca o registro completo.
  const handleViewDetails = async (employee) => {
    setSelectedEmployee(employee);
    setShowDetailsModal(true);
    try {
      const full = await apiGet(`/employees/${employee.id}/`);
      setSelectedEmployee(full);
    } catch (err) {
      console.error('Erro ao carregar detalhes do funcionário:', err);
    }
  };

  // ----------------------------------------------------------------
  // Acesso ao sistema de um funcionário JÁ cadastrado
  // ----------------------------------------------------------------
  // Antes só dava para criar o login no momento do cadastro. Quem foi
  // cadastrado sem acesso ficava sem conta para sempre — e, como a tela de
  // Atribuição partia da conta, o professor sumia da lista de quem pode
  // receber turma.
  // Perfis que este funcionário já tem. O backend ignora os repetidos e cria
  // só os que faltam, então o mesmo endpoint serve para criar o primeiro
  // acesso e para acrescentar um perfil a quem já tem.
  const perfisJaExistentes = linkedAccounts.map((c) => c.role);
  const perfisNovosSelecionados = userProfiles.filter(
    (p) => !perfisJaExistentes.includes(p)
  );

  const handleCreateAccess = async () => {
    if (!editingEmployee?.id || perfisNovosSelecionados.length === 0) return;
    if (!formData.cpf?.trim() && !formData.email?.trim()) {
      setFormErrors((prev) => ({
        ...prev,
        access: 'Preencha o CPF (ou ao menos o e-mail) e salve antes de criar o acesso.',
      }));
      return;
    }
    setCreatingAccess(true);
    setFormErrors((prev) => ({ ...prev, access: null }));
    try {
      const result = await apiPost(`/employees/${editingEmployee.id}/create_user/`, {
        roles: perfisNovosSelecionados,
        city: cityId,
      });
      setCreatedUserInfo(result);
      // Relê o cadastro em vez de remendar a lista na mão: com vários perfis
      // a resposta traz mais de uma conta, e o que vale é o que ficou gravado.
      try {
        const atualizado = await apiGet(`/employees/${editingEmployee.id}/`);
        setLinkedAccounts(atualizado.user_accounts || []);
      } catch {
        /* a listagem abaixo já recarrega */
      }
      setUserProfiles([]);
      fetchData();
    } catch (err) {
      console.error('Erro ao criar acesso:', err);
      setFormErrors((prev) => ({
        ...prev,
        access: err.response?.data?.error || 'Não foi possível criar o acesso.',
      }));
    } finally {
      setCreatingAccess(false);
    }
  };

  // ----------------------------------------------------------------
  // Duplicados
  // ----------------------------------------------------------------
  // Comparação sem acento e sem caixa (e com "Mª" = "Maria"), que é
  // justamente onde a busca falhava e a mesma pessoa acabava cadastrada
  // duas vezes.
  const findPossibleDuplicates = useCallback(
    (nome, ignoreId) => {
      const chave = normalizePersonName(nome);
      if (!chave || chave.length < 4) return [];
      return funcionarios.filter(
        (f) => f.id !== ignoreId && normalizePersonName(f.teacher_name) === chave
      );
    },
    [funcionarios]
  );

  const handleNameBlur = () => {
    const iguais = findPossibleDuplicates(formData.teacher_name, editingEmployee?.id);
    setDuplicateWarning(iguais.length ? iguais : null);
  };

  const handleOpenDuplicates = async () => {
    setShowDuplicatesPanel(true);
    setLoadingDuplicates(true);
    setDuplicatesError(null);
    try {
      const data = await apiGet('/employees/duplicates/');
      setDuplicateGroups(data?.groups || []);
    } catch (err) {
      console.error('Erro ao buscar duplicados:', err);
      setDuplicatesError('Não foi possível carregar os possíveis duplicados.');
    } finally {
      setLoadingDuplicates(false);
    }
  };

  // Funde `duplicado` em `titular`: vínculos, turmas, atribuições e conta de
  // acesso passam para o titular e o duplicado é excluído.
  const handleMerge = async (titular, duplicado) => {
    const ok = window.confirm(
      `Manter "${titular.teacher_name}" (matrícula ${titular.teacher_serial}) e ` +
      `excluir "${duplicado.teacher_name}" (matrícula ${duplicado.teacher_serial})?\n\n` +
      'Escolas, turmas, disciplinas e frequência do excluído passam para o cadastro mantido. ' +
      'Esta ação não pode ser desfeita.'
    );
    if (!ok) return;
    setMerging(`${titular.id}-${duplicado.id}`);
    setDuplicatesError(null);
    try {
      await apiPost(`/employees/${titular.id}/merge/`, { duplicate_id: duplicado.id });
      await handleOpenDuplicates();
      fetchData();
    } catch (err) {
      console.error('Erro ao mesclar cadastros:', err);
      setDuplicatesError(err.response?.data?.error || 'Não foi possível mesclar os cadastros.');
    } finally {
      setMerging(null);
    }
  };

  // Validação
  const validateForm = () => {
    const errs = {};
    if (!formData.teacher_name.trim()) errs.teacher_name = 'Nome é obrigatório';
    if (!formData.teacher_serial) errs.teacher_serial = 'Matrícula é obrigatória';
    if (!formData.id_role) errs.id_role = 'Cargo é obrigatório';
    if (!formData.employment_type) errs.employment_type = 'Tipo de vínculo é obrigatório';
    if (formData.schools.length === 0) errs.schools = 'Selecione pelo menos uma escola';
    if (formData.cpf) {
      const digits = formData.cpf.replace(/\D/g, '');
      if (digits.length !== 11) errs.cpf = 'CPF deve ter 11 dígitos';
    }
    // Administrador precisa de uma cidade selecionada para que o funcionário
    // (e o eventual acesso ao sistema) sejam vinculados à cidade correta.
    if (!editingEmployee && user?.role === ROLES.ADMIN && !cityId) {
      errs.submit = 'Selecione uma cidade no menu lateral antes de cadastrar o funcionário.';
    }
    if (!formData.email?.trim()) {
      errs.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = 'Email inválido';
    }
    if (!formData.phone?.trim()) {
      errs.phone = 'Telefone é obrigatório';
    } else if (formData.phone.replace(/\D/g, '').length < 10) {
      errs.phone = 'Telefone inválido';
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Salvar
  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload = {
        teacher_name: formData.teacher_name.trim(),
        teacher_serial: parseInt(formData.teacher_serial, 10),
        cpf: formData.cpf || null,
        rg: formData.rg || null,
        phone: formData.phone || null,
        email: formData.email || null,
        hire_date: formData.hire_date || null,
        id_role: formData.id_role || null,
        status: formData.status,
        employment_type: formData.employment_type || null,
        schools: formData.schools,
        // Disciplinas e turmas só se aplicam a cargos docentes. Enviamos lista
        // vazia nos demais casos para limpar vínculos de um cargo alterado.
        subjects: isProfessorRole ? formData.subjects : [],
        classes: isProfessorRole ? formData.classes : [],
        observations: formData.observations || null,
      };

      let savedEmployee;
      if (editingEmployee) {
        await apiPut(`/employees/${editingEmployee.id}/`, payload);
      } else {
        savedEmployee = await apiPost('/employees/', payload);
      }

      // Criar acesso ao sistema se solicitado (apenas no cadastro)
      if (!editingEmployee && createUserAccess && userProfiles.length > 0 && savedEmployee?.id) {
        try {
          const userResult = await apiPost(`/employees/${savedEmployee.id}/create_user/`, {
            roles: userProfiles,
            city: cityId,
          });
          setCreatedUserInfo(userResult);
          fetchData();
          return; // mantém modal aberto para exibir credenciais
        } catch (userErr) {
          console.error('Funcionário salvo, mas erro ao criar usuário:', userErr);
          setFormErrors((prev) => ({
            ...prev,
            submit: `Funcionário cadastrado, mas não foi possível criar o acesso: ${userErr.message}`,
          }));
          fetchData();
          return;
        }
      }

      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Erro ao salvar funcionário:', err, err.response?.data);
      const backendErrors = err.response?.data || {};
      const pickFirst = (val) => {
        if (!val) return null;
        if (typeof val === 'string') return val;
        if (Array.isArray(val)) return val[0];
        if (typeof val === 'object') {
          for (const k of Object.keys(val)) {
            const v = pickFirst(val[k]);
            if (v) return `${k}: ${v}`;
          }
        }
        return null;
      };
      const firstError =
        backendErrors.detail ||
        backendErrors.error ||
        pickFirst(backendErrors) ||
        err.message ||
        'Erro ao salvar funcionário. Verifique os dados e tente novamente.';
      setFormErrors((prev) => ({ ...prev, submit: firstError }));
    } finally {
      setSaving(false);
    }
  };

  // Excluir
  const handleDelete = async (item) => {
    if (!item) return;
    try {
      await apiDelete(`/employees/${item.id}/`);
      fetchData();
    } catch (err) {
      console.error('Erro ao excluir funcionário:', err);
      throw err;
    }
  };

  // Toggle escola no form
  const toggleSchool = (schoolId) => {
    setFormData((prev) => ({
      ...prev,
      schools: prev.schools.includes(schoolId)
        ? prev.schools.filter((id) => id !== schoolId)
        : [...prev.schools, schoolId],
      // Ao desmarcar uma escola, descarta as turmas dela que estavam marcadas
      classes: prev.schools.includes(schoolId)
        ? prev.classes.filter((cid) => {
            const t = turmas.find((x) => x.id === cid);
            return !t || t.id_school !== schoolId;
          })
        : prev.classes,
    }));
  };

  // Toggle disciplina no form (professores)
  const toggleSubject = (subjectId) => {
    setFormData((prev) => ({
      ...prev,
      subjects: prev.subjects.includes(subjectId)
        ? prev.subjects.filter((id) => id !== subjectId)
        : [...prev.subjects, subjectId],
    }));
  };

  // Toggle turma no form (professor regente)
  const toggleClass = (classId) => {
    setFormData((prev) => ({
      ...prev,
      classes: prev.classes.includes(classId)
        ? prev.classes.filter((id) => id !== classId)
        : [...prev.classes, classId],
    }));
  };

  // Bloqueio visual — Admin, Coordenador e Referência Técnica acessam o RH.
  const RH_ALLOWED_ROLES = [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA];
  if (!RH_ALLOWED_ROLES.includes(user?.role)) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-6 ${darkMode ? 'bg-[#0F172A] text-slate-200' : 'bg-gray-50 text-slate-800'}`}>
        <div className="text-center">
          <p className="text-lg font-semibold">Acesso restrito</p>
          <p className="text-sm opacity-70 mt-1">Esta área é exclusiva para Administrador, Coordenador e Referência Técnica.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinnerAdvanced message="Carregando funcionários..." darkMode={darkMode} />;
  }

  return (
    <div className={`min-h-screen p-4 sm:p-6 ${darkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
            RH — Funcionários
          </h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Gestão de professores, coordenadores, técnicos, auxiliares e demais funcionários
          </p>
        </div>
        <div className="flex items-center gap-3">
          <RefreshButton onClick={fetchData} />
          <a
            href="/funcionarios/frequencia"
            className="px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors flex items-center gap-2"
            title="Registrar frequência diária dos funcionários"
          >
            <span>🗓️</span>
            Frequência
          </a>
          <button
            onClick={handleOpenDuplicates}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${darkMode
              ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            title="Procurar cadastros repetidos da mesma pessoa"
          >
            <span>🧹</span>
            Duplicados
          </button>
          <button
            onClick={() => setImportando(true)}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${darkMode
              ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            title="Cadastrar vários funcionários de uma vez a partir de uma planilha"
          >
            <span>📥</span>
            Importar planilha
          </button>
          <button
            onClick={handleCreate}
            className="px-4 py-2.5 bg-[#0974BA] text-white rounded-lg text-sm font-medium hover:bg-[#0974BA]/90 transition-colors flex items-center gap-2"
          >
            <span>+</span>
            Novo Funcionário
          </button>
        </div>
      </div>

      {/* Toggle Dashboard / Lista / Encaminhamento */}
      <div className={`inline-flex p-1 rounded-xl mb-5 ${darkMode ? 'bg-slate-800/60 border border-slate-700' : 'bg-white border border-slate-200'}`}>
        {[
          { value: 'dashboard', label: 'Dashboard', icon: '📊' },
          { value: 'lista', label: 'Lista', icon: '📋' },
          { value: 'encaminhamento', label: 'Encaminhamento', icon: '📤' },
        ].map(opt => (
          <button
            key={opt.value}
            onClick={() => setView(opt.value)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              view === opt.value
                ? 'bg-[#0974BA] text-white shadow'
                : darkMode ? 'text-slate-300 hover:bg-slate-700/50' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <span>{opt.icon}</span>
            {opt.label}
          </button>
        ))}
      </div>

      {/* Dashboard */}
      {view === 'dashboard' && (
        <DashboardRH
          funcionarios={funcionarios}
          escolas={escolas}
          teacherSchools={teacherSchools}
          cargos={cargos}
          darkMode={darkMode}
        />
      )}

      {/* Encaminhamento (geração de PDF) */}
      {view === 'encaminhamento' && (
        <EncaminhamentoView
          funcionarios={funcionarios}
          escolas={escolas}
          teacherSchools={teacherSchools}
          darkMode={darkMode}
          currentUser={user}
        />
      )}

      {/* Resumo compacto (modo lista) */}
      {view === 'lista' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className={`p-4 rounded-xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
            <p className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total</p>
            <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-slate-800'}`}>{stats.total}</p>
          </div>
          <div className={`p-4 rounded-xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
            <p className={`text-xs font-medium ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>Ativos</p>
            <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>{stats.ativos}</p>
          </div>
          <div className={`p-4 rounded-xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
            <p className={`text-xs font-medium ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>Inativos</p>
            <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>{stats.inativos}</p>
          </div>
          <div className={`p-4 rounded-xl border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
            <p className={`text-xs font-medium ${darkMode ? 'text-[#0974BA]' : 'text-[#0974BA]'}`}>Cargos</p>
            <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-slate-800'}`}>{Object.keys(stats.porCargo).length}</p>
          </div>
        </div>
      )}

      {/* Erro */}
      {error && (
        <div className={`mb-6 p-4 rounded-xl border ${darkMode ? 'bg-[#E83A3C]/10 border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border-[#E83A3C]/20'}`}>
          <p className="text-[#E83A3C] text-sm">{error}</p>
          <button onClick={fetchData} className="text-[#0974BA] text-sm mt-2 hover:underline">Tentar novamente</button>
        </div>
      )}

      {/* Filtros (apenas no modo lista) */}
      {view === 'lista' && (
      <div className={`p-4 rounded-xl border mb-6 ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500'
                : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
              placeholder="Buscar por nome, matrícula, CPF ou email..."
            />
          </div>
          <SearchableSelect
            value={filtroCargo}
            onChange={setFiltroCargo}
            options={cargosAgrupados.flatMap(g =>
              g.items.map(c => ({ value: c.id, label: c.name, group: g.label }))
            )}
            emptyOptionLabel="Todos os cargos"
            darkMode={darkMode}
            className="min-w-[220px]"
          />
          <SearchableSelect
            value={filtroStatus}
            onChange={setFiltroStatus}
            options={STATUS_OPTIONS}
            emptyOptionLabel="Todos os status"
            darkMode={darkMode}
            className="min-w-[150px]"
          />
        </div>
      </div>
      )}

      {/* Tabela (apenas no modo lista) */}
      {view === 'lista' && (
      <div className={`rounded-xl border overflow-hidden ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={darkMode ? 'bg-slate-700/50' : 'bg-slate-50'}>
                <th className={`text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Funcionário</th>
                <th className={`text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Cargo</th>
                <th className={`text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Matrícula</th>
                <th className={`text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Contato</th>
                <th className={`text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Escolas</th>
                <th className={`text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Status</th>
                <th className={`text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Ações</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-100'}`}>
              {filteredFuncionarios.length === 0 ? (
                <tr>
                  <td colSpan="7" className={`text-center py-12 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    {searchTerm || filtroStatus || filtroCargo
                      ? 'Nenhum funcionário encontrado com os filtros aplicados.'
                      : 'Nenhum funcionário cadastrado.'}
                  </td>
                </tr>
              ) : (
                filteredFuncionarios.map((f) => {
                  const schoolList = getEmployeeSchools(f.id);
                  return (
                    <tr key={f.id} className={`transition-colors ${darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'}`}>
                      <td className={`py-3 px-4 ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${darkMode ? 'bg-[#0974BA]/20 text-[#0974BA]' : 'bg-[#0974BA]/10 text-[#0974BA]'}`}>
                            {f.teacher_name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <div className="font-medium text-sm flex items-center gap-2">
                              {f.teacher_name}
                              {/* Sem login: continua podendo receber turma, mas não
                                  entra no sistema. Antes isso ficava invisível. */}
                              {f.has_user === false && (
                                <span
                                  title="Funcionário sem login no sistema"
                                  className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${darkMode ? 'bg-slate-600/60 text-slate-300' : 'bg-slate-200 text-slate-600'}`}
                                >
                                  sem acesso
                                </span>
                              )}
                            </div>
                            {f.cpf && (
                              <div className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{f.cpf}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className={`py-3 px-4 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {f.role_name ? (
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${darkMode ? 'bg-[#0974BA]/20 text-[#0974BA]' : 'bg-[#0974BA]/10 text-[#0974BA]'}`}>
                            {f.role_name}
                          </span>
                        ) : (
                          <span className="text-xs opacity-50">-</span>
                        )}
                      </td>
                      <td className={`py-3 px-4 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{f.teacher_serial || '-'}</td>
                      <td className={`py-3 px-4 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {f.email && <div className="text-xs truncate max-w-[180px]" title={f.email}>{f.email}</div>}
                        {f.phone && <div className="text-xs">{f.phone}</div>}
                        {!f.email && !f.phone && <span className="text-xs opacity-50">-</span>}
                      </td>
                      <td className={`py-3 px-4 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {schoolList.length === 0 ? (
                          <span className="text-xs opacity-50">-</span>
                        ) : (
                          <>
                            {schoolList.slice(0, 2).map((s, idx) => (
                              <span key={s.id} className="text-xs">{s.name}{idx < Math.min(schoolList.length, 2) - 1 ? ', ' : ''}</span>
                            ))}
                            {schoolList.length > 2 && (
                              <span className={`text-xs ml-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>+{schoolList.length - 2}</span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${f.status === 'active'
                          ? darkMode ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600'
                          : f.status === 'inactive'
                          ? darkMode ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600'
                          : darkMode ? 'bg-sky-500/10 text-sky-400' : 'bg-sky-50 text-sky-600'
                          }`}>
                          {STATUS_LABELS[f.status] || f.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleViewDetails(f)}
                            className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                            title="Ver detalhes"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleEdit(f)}
                            className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                            title="Editar"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => openDeleteModal(f)}
                            className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-[#E83A3C]/10 text-slate-400 hover:text-[#E83A3C]' : 'hover:bg-[#E83A3C]/5 text-slate-400 hover:text-[#E83A3C]'}`}
                            title="Excluir"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className={`px-4 py-3 border-t ${darkMode ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-500'}`}>
          <p className="text-sm">Exibindo {filteredFuncionarios.length} de {stats.total} funcionários</p>
        </div>
      </div>
      )}

      {importando && (
        <ImportarFuncionariosModal
          escolas={escolas}
          cargos={cargos}
          cityId={cityId}
          darkMode={darkMode}
          onClose={() => setImportando(false)}
          onImported={() => fetchData({ silencioso: true })}
        />
      )}

      {/* Modal cadastro/edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-2xl rounded-2xl ${darkMode ? 'bg-slate-800' : 'bg-white'} shadow-2xl max-h-[90vh] overflow-y-auto`}>
            <div className={`p-6 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                  {editingEmployee ? 'Editar Funcionário' : 'Novo Funcionário'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Linha 1: Nome e Matrícula */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Nome Completo *</label>
                  <input
                    type="text"
                    value={formData.teacher_name}
                    onChange={(e) => setFormData({ ...formData, teacher_name: e.target.value })}
                    onBlur={handleNameBlur}
                    className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                      ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                      } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.teacher_name ? 'border-[#E83A3C]' : ''}`}
                    placeholder="Nome completo"
                  />
                  {formErrors.teacher_name && <p className="text-[#E83A3C] text-xs mt-1">{formErrors.teacher_name}</p>}
                  {duplicateWarning && (
                    <div className={`mt-2 rounded-lg p-2.5 text-xs ${darkMode ? 'bg-amber-500/10 text-amber-300' : 'bg-amber-50 text-amber-700'}`}>
                      <p className="font-medium">Já existe cadastro com este nome:</p>
                      <ul className="mt-1 space-y-0.5">
                        {duplicateWarning.map((f) => (
                          <li key={f.id}>
                            {f.teacher_name} — matrícula {f.teacher_serial}
                            {f.role_name ? ` (${f.role_name})` : ''}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-1 italic">
                        Confira antes de salvar para não duplicar a pessoa. Homônimos existem —
                        se for outra pessoa, pode continuar.
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Matrícula *</label>
                  <input
                    type="number"
                    value={formData.teacher_serial}
                    onChange={(e) => setFormData({ ...formData, teacher_serial: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                      ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                      } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.teacher_serial ? 'border-[#E83A3C]' : ''}`}
                    placeholder="Número de matrícula"
                  />
                  {formErrors.teacher_serial && <p className="text-[#E83A3C] text-xs mt-1">{formErrors.teacher_serial}</p>}
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Cargo *</label>
                  <SearchableSelect
                    value={formData.id_role}
                    onChange={(v) => setFormData({ ...formData, id_role: v ? parseInt(v, 10) : '' })}
                    options={cargosAgrupados.flatMap(g =>
                      g.items.map(c => ({ value: c.id, label: c.name, group: g.label }))
                    )}
                    placeholder="Selecione um cargo"
                    required
                    darkMode={darkMode}
                    className={`w-full ${formErrors.id_role ? 'ring-2 ring-[#E83A3C] rounded-lg' : ''}`}
                  />
                  {formErrors.id_role && <p className="text-[#E83A3C] text-xs mt-1">{formErrors.id_role}</p>}
                </div>
              </div>

              {/* Linha 2: CPF e RG */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>CPF</label>
                  <input
                    type="text"
                    value={formData.cpf}
                    onChange={(e) => setFormData({ ...formData, cpf: formatCpf(e.target.value) })}
                    className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                      ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                      } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.cpf ? 'border-[#E83A3C]' : ''}`}
                    placeholder="000.000.000-00"
                    maxLength={14}
                  />
                  {formErrors.cpf && <p className="text-[#E83A3C] text-xs mt-1">{formErrors.cpf}</p>}
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>RG</label>
                  <input
                    type="text"
                    value={formData.rg}
                    onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                      ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                      } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                    placeholder="RG"
                    maxLength={20}
                  />
                </div>
              </div>

              {/* Linha 3: Email e Telefone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                      ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                      } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.email ? 'border-[#E83A3C]' : ''}`}
                    placeholder="email@exemplo.com"
                  />
                  {formErrors.email && <p className="text-[#E83A3C] text-xs mt-1">{formErrors.email}</p>}
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Telefone *</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: formatPhone(e.target.value) })}
                    className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                      ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                      } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${formErrors.phone ? 'border-[#E83A3C]' : ''}`}
                    placeholder="(00) 00000-0000"
                    maxLength={15}
                  />
                  {formErrors.phone && <p className="text-[#E83A3C] text-xs mt-1">{formErrors.phone}</p>}
                </div>
              </div>

              {/* Linha 4: Data admissão e Status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Data de Admissão</label>
                  <input
                    type="date"
                    value={formData.hire_date}
                    onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                      ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                      } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Status</label>
                  <SearchableSelect
                    value={formData.status}
                    onChange={(v) => setFormData({ ...formData, status: v })}
                    options={STATUS_OPTIONS}
                    placeholder="Selecione"
                    darkMode={darkMode}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Tipo de Vínculo */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Tipo de Vínculo *</label>
                <SearchableSelect
                  value={formData.employment_type}
                  onChange={(v) => setFormData({ ...formData, employment_type: v })}
                  options={EMPLOYMENT_TYPE_OPTIONS}
                  placeholder="Selecione..."
                  required
                  darkMode={darkMode}
                  className={`w-full ${formErrors.employment_type ? 'ring-2 ring-[#E83A3C] rounded-lg' : ''}`}
                />
                {formErrors.employment_type && <p className="text-[#E83A3C] text-xs mt-1">{formErrors.employment_type}</p>}
              </div>

              {/* Escolas */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Escolas *</label>
                <div className={`p-3 rounded-lg border max-h-40 overflow-y-auto ${darkMode ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'} ${formErrors.schools ? 'border-[#E83A3C]' : ''}`}>
                  {escolas.length === 0 ? (
                    <p className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Nenhuma escola disponível</p>
                  ) : (
                    <div className="space-y-2">
                      {escolas.map((escola) => (
                        <label key={escola.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.schools.includes(escola.id)}
                            onChange={() => toggleSchool(escola.id)}
                            className="rounded border-slate-300 text-[#0974BA] focus:ring-[#0974BA]"
                          />
                          <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{escola.school}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {formErrors.schools && <p className="text-[#E83A3C] text-xs mt-1">{formErrors.schools}</p>}
              </div>

              {/* Disciplinas e Turmas — apenas para cargos docentes, para o
                  professor já sair pronto desta tela (sem passar por Professores
                  e pela atribuição de turmas). */}
              {isProfessorRole && (
                <>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Disciplinas</label>
                    <div className={`p-3 rounded-lg border max-h-40 overflow-y-auto ${darkMode ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                      {disciplinas.length === 0 ? (
                        <p className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Nenhuma disciplina cadastrada</p>
                      ) : (
                        <div className="space-y-2">
                          {disciplinas.map((disciplina) => (
                            <label key={disciplina.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.subjects.includes(disciplina.id)}
                                onChange={() => toggleSubject(disciplina.id)}
                                className="rounded border-slate-300 text-[#0974BA] focus:ring-[#0974BA]"
                              />
                              <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{disciplina.subject_name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      Turmas <span className={`font-normal ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>(como professor regente)</span>
                    </label>
                    <div className={`p-3 rounded-lg border max-h-40 overflow-y-auto ${darkMode ? 'bg-slate-700/30 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
                      {formData.schools.length === 0 ? (
                        <p className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Selecione uma escola para listar as turmas</p>
                      ) : turmasDisponiveis.length === 0 ? (
                        <p className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Nenhuma turma nas escolas selecionadas</p>
                      ) : (
                        <div className="space-y-2">
                          {turmasDisponiveis.map((turma) => (
                            <label key={turma.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.classes.includes(turma.id)}
                                onChange={() => toggleClass(turma.id)}
                                className="rounded border-slate-300 text-[#0974BA] focus:ring-[#0974BA]"
                              />
                              <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                {turma.class_name}
                                {turma.school_name ? ` — ${turma.school_name}` : ''}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Observações */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Observações</label>
                <textarea
                  value={formData.observations}
                  onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                  rows={3}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 resize-none`}
                  placeholder="Anotações internas sobre o funcionário..."
                />
              </div>

              {/* Acesso ao Sistema — na EDIÇÃO de um funcionário já cadastrado.
                  Sem isto, quem foi cadastrado sem acesso nunca conseguia um
                  login depois — e a tela de Atribuição partia do login. */}
              {editingEmployee && !createdUserInfo && (
                <div className={`rounded-xl border p-4 ${darkMode ? 'border-slate-600 bg-slate-700/30' : 'border-slate-200 bg-slate-50'}`}>
                  <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>Acesso ao sistema</p>
                  <div className="mt-3 space-y-3">
                    {linkedAccounts.length > 0 ? (
                      <div className={`text-xs space-y-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        <p className="font-medium">
                          {linkedAccounts.length === 1
                            ? 'Acesso já criado:'
                            : `${linkedAccounts.length} acessos já criados (um por perfil):`}
                        </p>
                        {linkedAccounts.map((conta) => (
                          <p key={conta.id} className="flex flex-wrap items-center gap-x-2">
                            <code className={`px-1.5 py-0.5 rounded ${darkMode ? 'bg-slate-700' : 'bg-white'}`}>
                              {conta.username}
                            </code>
                            <span>{conta.role_display || conta.role}</span>
                            {conta.is_active === false && (
                              <span className="text-[#E83A3C]">(desativado)</span>
                            )}
                          </p>
                        ))}
                        {linkedAccounts.length > 1 && (
                          <p className={`italic ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            No login ela escolhe com qual entrar, e troca de perfil pelo
                            menu lateral sem sair do sistema.
                          </p>
                        )}
                        {/* O que liga um acesso ao outro é o CPF — e só ele.
                            Sem CPF os logins existem mas ficam soltos: não
                            aparece a escolha no login nem a troca no menu. */}
                        {linkedAccounts.length > 1 && !formData.cpf?.trim() && (
                          <p className="text-[#F9B047]">
                            Sem CPF no cadastro, estes acessos não se reconhecem como da
                            mesma pessoa: ela terá de sair e entrar para trocar de perfil.
                            Preencha o CPF e salve.
                          </p>
                        )}
                        <p className={`italic ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Para redefinir a senha, use a tela Sistema &gt; Usuários.
                        </p>
                      </div>
                    ) : (
                      <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Este funcionário ainda não tem login. Criar o acesso não é obrigatório
                        para receber turma, mas é necessário para ele entrar no sistema.
                      </p>
                    )}

                    <div>
                        <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {linkedAccounts.length > 0 ? 'Acrescentar perfil de acesso' : 'Perfis de acesso'}
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {SYSTEM_ROLE_OPTIONS.filter(
                            (o) => o.value !== 'administrador' || user?.role === ROLES.ADMIN
                          ).map((o) => {
                            // Perfil que a pessoa já tem aparece marcado e
                            // travado: some a dúvida de "preciso remarcar?" e
                            // não há como pedir a criação de algo que existe.
                            const jaTem = perfisJaExistentes.includes(o.value);
                            const marcado = jaTem || userProfiles.includes(o.value);
                            return (
                              <label
                                key={o.value}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                                  jaTem ? 'cursor-default opacity-70' : 'cursor-pointer'
                                } ${
                                  marcado
                                    ? 'border-[#0974BA] bg-[#0974BA]/10'
                                    : darkMode
                                      ? 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                                      : 'bg-white border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={marcado}
                                  disabled={jaTem}
                                  onChange={() => alternarPerfilAcesso(o.value)}
                                  className="w-4 h-4 accent-[#0974BA]"
                                />
                                <span className={`text-xs ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                                  {o.label}
                                  {jaTem && (
                                    <span className={darkMode ? 'text-slate-400' : 'text-slate-500'}> · já tem</span>
                                  )}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                        {perfisNovosSelecionados.length > 0 && (
                          <p className={`text-xs mt-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Cada perfil vira um login separado, com a mesma senha inicial.
                            No login ela escolhe com qual entrar.
                          </p>
                        )}
                      </div>
                      {formErrors.access && (
                        <p className="text-[#E83A3C] text-xs">{formErrors.access}</p>
                      )}
                      <button
                        type="button"
                        onClick={handleCreateAccess}
                        disabled={perfisNovosSelecionados.length === 0 || creatingAccess}
                        className="px-3 py-2 bg-[#0974BA] text-white rounded-lg text-xs font-medium hover:bg-[#0974BA]/90 transition-colors disabled:opacity-50"
                      >
                        {creatingAccess
                          ? 'Criando acesso...'
                          : linkedAccounts.length > 0
                            ? `Criar ${perfisNovosSelecionados.length || ''} acesso(s) a mais`.replace('  ', ' ')
                            : 'Criar acesso ao sistema'}
                      </button>
                    </div>
                </div>
              )}

              {/* Acesso ao Sistema — somente no cadastro */}
              {!editingEmployee && !createdUserInfo && (
                <div className={`rounded-xl border p-4 ${darkMode ? 'border-slate-600 bg-slate-700/30' : 'border-slate-200 bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div>
                      <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>Criar acesso ao sistema</p>
                      <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Permite que o funcionário faça login no SIDEB
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCreateUserAccess((v) => !v)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                        createUserAccess ? 'bg-[#0974BA]' : darkMode ? 'bg-slate-600' : 'bg-slate-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${createUserAccess ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>

                  {createUserAccess && (
                    <div className="mt-3 space-y-3">
                      <div>
                        <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Perfis de acesso *
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {SYSTEM_ROLE_OPTIONS.filter(
                            (o) => o.value !== 'administrador' || user?.role === ROLES.ADMIN
                          ).map((o) => {
                            const marcado = userProfiles.includes(o.value);
                            return (
                              <label
                                key={o.value}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                                  marcado
                                    ? 'border-[#0974BA] bg-[#0974BA]/10'
                                    : darkMode
                                      ? 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                                      : 'bg-white border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={marcado}
                                  onChange={() => alternarPerfilAcesso(o.value)}
                                  className="w-4 h-4 accent-[#0974BA]"
                                />
                                <span className={`text-xs ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                                  {o.label}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                      <div className={`flex items-start gap-2 text-xs rounded-lg p-2.5 ${darkMode ? 'bg-[#0974BA]/10 text-slate-300' : 'bg-[#0974BA]/5 text-slate-600'}`}>
                        <svg className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#0974BA]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>
                          Com CPF, o <strong>login</strong> e a <strong>senha inicial</strong> são o CPF (somente dígitos).
                          Sem CPF, o login sai do e-mail e a senha inicial é <strong>Sideb@2025</strong>.
                          O funcionário deverá alterar a senha no primeiro acesso.
                          {userProfiles.length > 1 && (
                            <>
                              {' '}Com <strong>{userProfiles.length} perfis</strong>, o login só pode ser
                              o CPF em um deles: os outros levam o perfil no fim
                              (ex.: <span className="font-mono">00000000000.secretario</span>).
                              A senha inicial é a mesma nos dois, então no login ele escolhe
                              com qual acesso quer entrar.
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Painel de credenciais criadas */}
              {createdUserInfo && (
                <div className={`rounded-xl border p-4 ${darkMode ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-emerald-200 bg-emerald-50'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className={`text-sm font-semibold ${darkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                      {editingEmployee ? 'Acesso criado!' : 'Funcionário cadastrado e acesso criado!'}
                    </p>
                  </div>
                  <div className={`text-xs space-y-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    {(createdUserInfo.accounts?.length > 1
                      ? createdUserInfo.accounts
                      : [{ username: createdUserInfo.username, role_display: null }]
                    ).map((conta) => (
                      <p key={conta.username}>
                        <span className="font-medium">
                          {conta.role_display ? `Login (${conta.role_display}):` : 'Login:'}
                        </span>{' '}
                        <code className={`px-1.5 py-0.5 rounded ${darkMode ? 'bg-slate-700' : 'bg-white'}`}>{conta.username}</code>
                      </p>
                    ))}
                    <p><span className="font-medium">Senha inicial:</span> <code className={`px-1.5 py-0.5 rounded ${darkMode ? 'bg-slate-700' : 'bg-white'}`}>{createdUserInfo.temp_password}</code></p>
                    <p className={`mt-2 italic ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Anote estas credenciais e repasse ao funcionário. A senha deve ser alterada no primeiro acesso.
                      {createdUserInfo.accounts?.length > 1 && (
                        <> A senha inicial é a mesma nos {createdUserInfo.accounts.length} acessos;
                        no login ele escolhe com qual quer entrar.</>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {formErrors.submit && (
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
                  <p className="text-[#E83A3C] text-sm">{formErrors.submit}</p>
                </div>
              )}
            </div>

            <div className={`p-6 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'} flex justify-end gap-3`}>
              <button
                onClick={() => { setShowModal(false); setCreatedUserInfo(null); }}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${darkMode
                  ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
              >
                {createdUserInfo ? 'Fechar' : 'Cancelar'}
              </button>
              {(!createdUserInfo || editingEmployee) && (
                <button
                  onClick={handleSave}
                  disabled={saving || loadingEmployee || (createUserAccess && userProfiles.length === 0)}
                  className="px-4 py-2.5 bg-[#0974BA] text-white rounded-lg text-sm font-medium hover:bg-[#0974BA]/90 transition-colors disabled:opacity-50"
                >
                  {loadingEmployee
                    ? 'Carregando...'
                    : saving
                    ? 'Salvando...'
                    : editingEmployee
                      ? 'Atualizar'
                      : createUserAccess
                        ? 'Cadastrar e criar acesso'
                        : 'Cadastrar'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal detalhes */}
      {showDetailsModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-lg rounded-2xl ${darkMode ? 'bg-slate-800' : 'bg-white'} shadow-2xl max-h-[90vh] overflow-y-auto`}>
            <div className={`p-6 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>Detalhes do Funcionário</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold ${darkMode ? 'bg-[#0974BA]/20 text-[#0974BA]' : 'bg-[#0974BA]/10 text-[#0974BA]'}`}>
                  {selectedEmployee.teacher_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>{selectedEmployee.teacher_name}</h3>
                  <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {selectedEmployee.role_name || 'Sem cargo'} • Matrícula: {selectedEmployee.teacher_serial}
                  </p>
                </div>
              </div>

              <div className={`grid grid-cols-2 gap-4 p-4 rounded-lg ${darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
                <div>
                  <p className={`text-xs font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Status</p>
                  <span className={`inline-block mt-1 px-2.5 py-1 rounded-full text-xs font-medium ${selectedEmployee.status === 'active'
                    ? darkMode ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600'
                    : selectedEmployee.status === 'inactive'
                    ? darkMode ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600'
                    : darkMode ? 'bg-sky-500/10 text-sky-400' : 'bg-sky-50 text-sky-600'
                    }`}>
                    {STATUS_LABELS[selectedEmployee.status] || selectedEmployee.status}
                  </span>
                </div>
                <div>
                  <p className={`text-xs font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Data de admissão</p>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    {selectedEmployee.hire_date ? new Date(selectedEmployee.hire_date).toLocaleDateString('pt-BR') : '-'}
                  </p>
                </div>
                <div>
                  <p className={`text-xs font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>CPF</p>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{selectedEmployee.cpf || '-'}</p>
                </div>
                <div>
                  <p className={`text-xs font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>RG</p>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{selectedEmployee.rg || '-'}</p>
                </div>
                <div>
                  <p className={`text-xs font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Email</p>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{selectedEmployee.email || '-'}</p>
                </div>
                <div>
                  <p className={`text-xs font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Telefone</p>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{selectedEmployee.phone || '-'}</p>
                </div>
              </div>

              <div>
                <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Escolas</p>
                <div className="space-y-1">
                  {getEmployeeSchools(selectedEmployee.id).length > 0 ? (
                    getEmployeeSchools(selectedEmployee.id).map((s) => (
                      <p key={s.id} className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{s.name}</p>
                    ))
                  ) : (
                    <span className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Nenhuma escola vinculada</span>
                  )}
                </div>
              </div>

              {selectedEmployee.observations && (
                <div>
                  <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>Observações</p>
                  <p className={`text-sm whitespace-pre-line ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{selectedEmployee.observations}</p>
                </div>
              )}
            </div>

            <div className={`p-6 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'} flex justify-end`}>
              <button
                onClick={() => setShowDetailsModal(false)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${darkMode
                  ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Painel de possíveis duplicados */}
      {showDuplicatesPanel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className={`w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl ${darkMode ? 'bg-slate-800' : 'bg-white'}`}>
            <div className={`p-6 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'} flex items-start justify-between gap-4`}>
              <div>
                <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                  Possíveis cadastros duplicados
                </h3>
                <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Agrupados por nome (ignorando acento e maiúsculas) e por CPF.
                  Escolha qual cadastro fica — o outro é excluído e tudo o que
                  estava nele passa para o mantido.
                </p>
              </div>
              <button
                onClick={() => setShowDuplicatesPanel(false)}
                className={`text-xl leading-none ${darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'}`}
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              {duplicatesError && (
                <div className="p-3 rounded-lg bg-[#E83A3C]/10 border border-[#E83A3C]/20">
                  <p className="text-[#E83A3C] text-sm">{duplicatesError}</p>
                </div>
              )}

              {loadingDuplicates ? (
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Procurando...</p>
              ) : duplicateGroups.length === 0 ? (
                <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Nenhum cadastro repetido encontrado.
                </p>
              ) : (
                duplicateGroups.map((grupo, idx) => (
                  <div
                    key={`${grupo.reason}-${grupo.key}-${idx}`}
                    className={`rounded-xl border p-4 ${darkMode ? 'border-slate-700 bg-slate-700/20' : 'border-slate-200 bg-slate-50'}`}
                  >
                    <p className={`text-xs uppercase tracking-wider mb-3 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      Mesmo {grupo.reason === 'cpf' ? 'CPF' : 'nome'}
                    </p>
                    <div className="space-y-3">
                      {grupo.employees.map((emp) => (
                        <div
                          key={emp.id}
                          className={`rounded-lg p-3 ${darkMode ? 'bg-slate-800' : 'bg-white'} border ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-[240px]">
                              <p className={`text-sm font-medium ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                {emp.teacher_name}
                              </p>
                              <p className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                matrícula {emp.teacher_serial}
                                {emp.cpf ? ` • ${emp.cpf}` : ''}
                                {emp.role_name ? ` • ${emp.role_name}` : ''}
                              </p>
                              <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                {emp.school_count} escola(s) • {emp.class_count} turma(s) como regente •{' '}
                                {emp.assignment_count} atribuição(ões) • {emp.has_user ? 'com login' : 'sem login'}
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {grupo.employees
                                .filter((outro) => outro.id !== emp.id)
                                .map((outro) => (
                                  <button
                                    key={outro.id}
                                    onClick={() => handleMerge(emp, outro)}
                                    disabled={merging === `${emp.id}-${outro.id}`}
                                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-[#0974BA] text-white hover:bg-[#0974BA]/90 transition-colors disabled:opacity-50"
                                    title={`Manter este cadastro e excluir "${outro.teacher_name}"`}
                                  >
                                    {merging === `${emp.id}-${outro.id}`
                                      ? 'Mesclando...'
                                      : `Manter este e excluir matrícula ${outro.teacher_serial}`}
                                  </button>
                                ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className={`p-6 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'} flex justify-end`}>
              <button
                onClick={() => setShowDuplicatesPanel(false)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium ${darkMode
                  ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmação de exclusão */}
      {/* Modal confirmação de exclusão */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        item={itemToDelete}
        itemName={itemToDelete?.teacher_name}
        itemType="Funcionário"
        warningMessage="Todos os dados vinculados a este funcionário serão afetados. Digite sua senha para confirmar."
      />
    </div>
  );
}

// ============================================================
// Encaminhamento — geração de declaração em PDF
// ============================================================
function EncaminhamentoView({ funcionarios, escolas, teacherSchools, darkMode, currentUser }) {
  const [idTeacher, setIdTeacher] = useState('');
  const [idOrigin, setIdOrigin] = useState('');
  const [idDestination, setIdDestination] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [reason, setReason] = useState('');
  const [signerName, setSignerName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const funcionariosAtivos = useMemo(
    () => (funcionarios || []).filter(f => f.status === 'active'),
    [funcionarios]
  );

  // Quando muda o servidor, sugere a unidade ativa como origem.
  useEffect(() => {
    if (!idTeacher) {
      setIdOrigin('');
      return;
    }
    const tid = parseInt(idTeacher, 10);
    const active = (teacherSchools || []).find(
      ts => ts.id_teacher === tid && ts.status === 'active'
    );
    setIdOrigin(active ? String(active.id_school) : '');
  }, [idTeacher, teacherSchools]);

  const handleGenerate = async () => {
    setError(null);
    setSuccess(null);
    if (!idTeacher) {
      setError('Selecione o(a) servidor(a) a ser encaminhado(a).');
      return;
    }
    if (!idDestination) {
      setError('Selecione a unidade de destino.');
      return;
    }
    if (idOrigin && String(idOrigin) === String(idDestination)) {
      setError('A unidade de destino deve ser diferente da unidade de origem.');
      return;
    }
    if (!startDate) {
      setError('Informe a data prevista para início.');
      return;
    }
    if (!reason.trim()) {
      setError('Descreva o motivo do encaminhamento.');
      return;
    }

    setGenerating(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/teachers/${idTeacher}/encaminhamento-pdf/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_origin_school: idOrigin ? parseInt(idOrigin, 10) : null,
          id_destination_school: parseInt(idDestination, 10),
          start_date: startDate,
          reason: reason.trim(),
          signer_name: signerName.trim() || null,
        }),
      });

      if (!resp.ok) {
        let msg = `Erro ao gerar PDF (${resp.status}).`;
        try {
          const data = await resp.json();
          msg = data.error || data.detail || msg;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }

      const blob = await resp.blob();
      // Abre nova aba para impressão direta + força download
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');

      const disposition = resp.headers.get('Content-Disposition') || '';
      const m = /filename="?([^";]+)"?/i.exec(disposition);
      const a = document.createElement('a');
      a.href = url;
      a.download = m ? m[1] : `encaminhamento_${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);

      setSuccess('Declaração gerada com sucesso. A nova aba abriu para impressão.');
    } catch (err) {
      console.error('Erro ao gerar encaminhamento:', err);
      setError(err.message || 'Não foi possível gerar a declaração.');
    } finally {
      setGenerating(false);
    }
  };

  const inputBase = `w-full px-4 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${
    darkMode
      ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-500'
      : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
  }`;

  return (
    <div className={`rounded-xl p-6 border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}>
      <header className="mb-6">
        <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
          Declaração de Encaminhamento
        </h2>
        <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          Selecione o(a) servidor(a), as unidades de origem e destino, a data de início
          e o motivo. O sistema gera a declaração em PDF para impressão.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Servidor */}
        <div className="md:col-span-2">
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            Servidor(a) *
          </label>
          <SearchableSelect
            value={idTeacher}
            onChange={setIdTeacher}
            options={funcionariosAtivos.map(f => ({
              value: f.id,
              label: `${f.teacher_name} • mat. ${f.teacher_serial}`,
            }))}
            placeholder="Busque pelo nome ou matrícula..."
            darkMode={darkMode}
            className="w-full"
          />
        </div>

        {/* Origem */}
        <div>
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            Unidade de origem
          </label>
          <SearchableSelect
            value={idOrigin}
            onChange={setIdOrigin}
            options={(escolas || []).map(e => ({
              value: e.id,
              label: e.is_secretaria ? `${e.school} (Secretaria)` : e.school,
            }))}
            placeholder="Selecione a origem..."
            darkMode={darkMode}
            className="w-full"
          />
          <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            Preenchida automaticamente pela lotação ativa quando disponível.
          </p>
        </div>

        {/* Destino */}
        <div>
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            Unidade de destino *
          </label>
          <SearchableSelect
            value={idDestination}
            onChange={setIdDestination}
            options={(escolas || []).map(e => ({
              value: e.id,
              label: e.is_secretaria ? `${e.school} (Secretaria)` : e.school,
            }))}
            placeholder="Selecione o destino..."
            darkMode={darkMode}
            className="w-full"
          />
        </div>

        {/* Data */}
        <div>
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            Data prevista para início *
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className={inputBase}
          />
        </div>

        {/* Assinante */}
        <div>
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            Nome do(a) Secretário(a) (assinatura)
          </label>
          <input
            type="text"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            placeholder="Opcional — deixe em branco para apenas a linha de assinatura"
            className={inputBase}
          />
        </div>

        {/* Motivo */}
        <div className="md:col-span-2">
          <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            Motivo do encaminhamento *
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder="Descreva o motivo (ex.: remanejamento, substituição, complementação de carga horária, etc.)"
            className={inputBase}
          />
        </div>
      </div>

      {error && (
        <div className={`mt-4 p-3 rounded-lg text-sm ${darkMode
          ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20 text-[#E83A3C]'
          : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20 text-[#E83A3C]'
        }`}>
          {error}
        </div>
      )}

      {success && (
        <div className={`mt-4 p-3 rounded-lg text-sm ${darkMode
          ? 'bg-[#13983D]/10 border border-[#13983D]/30 text-[#13983D]'
          : 'bg-[#13983D]/5 border border-[#13983D]/20 text-[#13983D]'
        }`}>
          {success}
        </div>
      )}

      <div className="flex justify-end mt-6">
        <button
          onClick={handleGenerate}
          disabled={generating}
          className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${
            generating
              ? 'bg-slate-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg'
          }`}
        >
          {generating ? 'Gerando PDF...' : 'Gerar e imprimir'}
        </button>
      </div>

      {currentUser?.role && !['administrador', 'coordenador'].includes(currentUser.role) && (
        <p className={`mt-3 text-xs ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
          Apenas Administrador e Coordenador podem emitir esta declaração.
        </p>
      )}
    </div>
  );
}

export default Funcionarios;
